// src/utils/prompts.js
// Centraliza AQUÍ todo tu texto repetitivo y construcción de prompts ↓↓↓

/* ---------- ESQUEMA DE BASE DE DATOS ADAPTADO ---------- */
export const DB_SCHEMA = `
ESQUEMA DE BASE DE DATOS:

public.datos_mercado (
  fecha TEXT,                         -- Fecha de los datos registrados (ej. '2024-01-15')
  id_producto TEXT,                   -- Identificador único para cada producto
  canal TEXT,                         -- Canal de ventas (ej. supermercado, farmacia)
  precio_sin_descuento DOUBLE PRECISION, -- Precio regular del producto antes de descuentos
  mecanica_promocional TEXT,          -- Descripción de la estrategia promocional
  precio_venta DOUBLE PRECISION,      -- Precio de venta real después de promociones
  subcategoria TEXT,                  -- Subcategoría del producto
  descripcion_producto TEXT,          -- Descripción del producto o características
  marca TEXT,                         -- Nombre de la marca del producto
  llave TEXT,                         -- Clave para unir con la tabla mis_datos
  categoria TEXT                      -- Categoría principal del producto
);

public.mis_datos (
  fecha TEXT,                         -- Fecha de los datos registrados (ej. '2024-12-31')
  id_producto TEXT,                   -- Identificador único para cada producto en tu portafolio
  precio_sin_descuento DOUBLE PRECISION, -- Precio regular del producto antes de descuentos
  precio_venta DOUBLE PRECISION,      -- Precio de venta final después de promociones
  unidades_vendidas BIGINT,           -- Total de unidades vendidas del producto
  margen DOUBLE PRECISION,            -- Margen de ganancia del producto
  descripcion_producto TEXT,          -- Descripción del producto o características
  llave TEXT,                         -- Clave para unir con la tabla datos_mercado
  categoria TEXT                      -- Categoría principal del producto
);

RELACIONES PRINCIPALES:
- mis_datos y datos_mercado se pueden relacionar por: llave, id_producto.

IMPORTANTE - TIPOS DE DATOS:
- fecha: Es TEXT, NO DATE. Para operaciones de fecha usar CAST(fecha AS DATE).
- Campos numéricos: precio_sin_descuento, precio_venta, unidades_vendidas, margen son numéricos.
- Campos de texto: categoria, marca, canal, etc. son TEXT.

NOTAS CRÍTICAS PARA FECHAS:
- Las columnas 'fecha' son TEXT, no DATE.
- Para extraer año: DATE_PART('year', CAST(fecha AS DATE)) AS año.
- Para extraer mes: DATE_PART('month', CAST(fecha AS DATE)) AS mes.
- Para filtros por fecha: WHERE CAST(fecha AS DATE) >= '2024-01-01'.
- Para rangos de fechas: WHERE CAST(fecha AS DATE) BETWEEN '2024-01-01' AND '2024-12-31'.
`;

/* ---------- BUILDERS DE PROMPTS ---------- */
export const buildWebSearchPrompt = (userQuery) => `
Eres un asistente especializado en búsqueda web que complementa a un sistema de análisis de datos para retail. Tu función es ÚNICAMENTE buscar información externa específica que NO está disponible en la base de datos interna.

ANÁLISIS DE LA CONSULTA:
Primero analiza la pregunta del usuario e identifica QUÉ información específica necesita ser buscada en internet vs qué puede ser respondido con datos internos.

INFORMACIÓN QUE DEBES BUSCAR (externa/actualizada):
- Precios de commodities
- Tasas de cambio actuales
- Inflación y datos macroeconómicos recientes
- Noticias de mercado y eventos económicos
- Benchmarks de industria y competidores externos
- Regulaciones o cambios normativos recientes
- Tendencias globales de consumo
- Datos de mercados internacionales

INFORMACIÓN QUE NO DEBES BUSCAR (interna de la empresa):
- Ventas propias, inventarios, precios internos
- Datos históricos de la empresa
- Información de canales, productos específicos de la empresa
- Márgenes, métricas internas

INSTRUCCIONES:
1. Si la pregunta SOLO requiere datos internos → Responde: "No se requiere búsqueda web. El agente principal puede responder con datos internos."

2. Si la pregunta requiere información externa específica → Busca ÚNICAMENTE esa información y proporciona:
    - Datos específicos encontrados (números, fechas, valores exactos)
    - Fuente y fecha de la información
    - Contexto breve si es relevante

3. Si no encuentras la información específica → Responde: "No se pudo encontrar información actualizada sobre [tema específico]. El agente principal debe proceder solo con datos internos."

FORMATO DE RESPUESTA:
Si encuentras información:
"INFORMACIÓN EXTERNA ENCONTRADA:
[Dato específico]: [Valor] ([Fuente], [Fecha])
[Contexto breve si es necesario]"

Sé preciso, conciso y enfócate solo en la información externa necesaria, sé lo más breve posible sólo contesta lo que se te pide.

CONSULTA DEL USUARIO: ${userQuery}
`;

export const buildSuggestedQuestionsPrompt = (prevQ, botResp) => `
Estoy usando un asistente de análisis de datos para retail con PostgreSQL. Por favor, genera 3 preguntas de seguimiento relevantes basadas en la pregunta anterior y la respuesta obtenida. 
              
${DB_SCHEMA}

Pregunta anterior: "${prevQ}"
Respuesta: "${botResp}"

Instrucciones:
1. Las preguntas deben ser para profundizar en el análisis y descubrir insights adicionales
2. Deben ser específicas y relacionadas con el tema de la pregunta original
3. Deben poder responderse con las tablas disponibles en el esquema
4. Deben ser breves y estar listas para copiar y pegar

IMPORTANTE: Responde SOLO con las 3 preguntas en formato estrictamente JSON como el siguiente:
["pregunta 1", "pregunta 2", "pregunta 3"]

No incluyas otra explicación, solo el array JSON.
`;

export const buildConsultClaudePrompt = (origQuery, errorMsg) => `
Eres un asistente especializado en análisis de datos para retail. El usuario está intentando 
obtener información de una base de datos PostgreSQL con el siguiente esquema:

${DB_SCHEMA}

Ha ocurrido un error con la consulta:
"${origQuery}"
Error devuelto:
"${errorMsg}"

Por favor, proporciona 2-3 alternativas para reformular mejor esta consulta. 
Para cada alternativa, proporciona:
1. Una pregunta reformulada más específica y clara
2. Una breve justificación de por qué esta alternativa podría funcionar mejor

Devuelve solo las alternativas en formato de lista numerada, sin introducción ni conclusión.
Cada alternativa debe ser concisa y directa, enfocándose en obtener datos relevantes que el sistema pueda procesar.
`;

export const buildSQLPrompt = (userContent, mensajesJSON) => { return `
Eres un agente especializado en optimizar consultas SQL para PostgreSQL basado en la respuesta de otro agente buscador, principalmente estas consultas son para generar tablas de excel con los datos.

${DB_SCHEMA}

Consulta del usuario:
${userContent}

Consultas de referencia:
${mensajesJSON}

⚠️ REGLAS CRÍTICAS PARA GARANTIZAR RESULTADOS CON DATOS:

🔴 **REGLAS DE EXISTENCIA DE DATOS (CRÍTICAS)**:

1. **BÚSQUEDAS FLEXIBLES EN TEXTO**:
   - ✅ USAR: categoria ILIKE '%Farmacia%' (busca "farmacia", "FARMACIA", "Productos farmacia", etc.)
   - ❌ EVITAR: categoria = 'Farmacia' (solo encuentra coincidencias exactas)
   - ✅ USAR: marca ILIKE '%nike%' OR descripcion_producto ILIKE '%nike%'
   - ✅ USAR: UPPER(categoria) LIKE UPPER('%farmacia%') para mayor flexibilidad

2. **FILTROS NUMÉRICOS INCLUSIVOS**:
   - ✅ USAR: precio_venta >= 0 (incluye productos gratuitos/promocionales)
   - ❌ EVITAR: precio_venta > 0 (excluye registros con valor 0)
   - ✅ USAR: unidades_vendidas >= 0 (incluye registros con 0 ventas)
   - ✅ USAR: margen >= -100 (incluye productos con pérdida)

3. **PERÍODOS DE TIEMPO AMPLIOS**:
   - ✅ POR DEFECTO: INTERVAL '12 months' (un año completo)
   - ✅ PARA ANÁLISIS DETALLADO: INTERVAL '24 months' (dos años)
   - ❌ EVITAR: INTERVAL '1 month' o '3 months' (muy restrictivo)
   - ✅ USAR: INTERVAL '6 months' MÍNIMO para análisis específicos

4. **HAVING CLAUSES FLEXIBLES**:
   - ✅ USAR: HAVING COUNT(*) > 0 (cualquier registro)
   - ✅ USAR: HAVING SUM(CAST(unidades_vendidas AS NUMERIC)) >= 0
   - ❌ EVITAR: HAVING COUNT(*) > 10 o SUM(campo) > 100 (muy restrictivo)

5. **ESTRATEGIA DE VERIFICACIÓN PROGRESIVA**:
   - PASO 1: Usar filtros mínimos primero (solo fecha e IS NOT NULL)
   - PASO 2: Agregar filtros de calidad gradualmente
   - PASO 3: Si no hay datos, expandir criterios automáticamente

⚠️ REGLAS CRÍTICAS DE OPTIMIZACIÓN Y RENDIMIENTO:

1. **FILTROS TEMPORALES OBLIGATORIOS**: 
   - SIEMPRE incluir filtros de fecha para evitar procesar años completos
   - Por defecto usar: WHERE CAST(fecha AS DATE) >= CURRENT_DATE - INTERVAL '12 months'
   - Para análisis específicos: WHERE CAST(fecha AS DATE) >= CURRENT_DATE - INTERVAL '6 months'
   - Para tendencias: WHERE CAST(fecha AS DATE) >= CURRENT_DATE - INTERVAL '24 months'

2. **FILTROS DE CALIDAD DE DATOS FLEXIBLES**:
   - SIEMPRE filtrar valores nulos: WHERE campo IS NOT NULL
   - Para campos numéricos: WHERE unidades_vendidas >= 0, WHERE precio_venta >= 0
   - Para análisis de ventas: WHERE unidades_vendidas >= 0 AND precio_venta >= 0

3. **LÍMITES DE RESULTADOS INTELIGENTES**:
   - Para análisis generales: LIMIT 2000 (incrementado)
   - Para análisis detallados: LIMIT 5000
   - Para exports completos: LIMIT 10000 (máximo absoluto)
   - Para TOP rankings: LIMIT 100 o LIMIT 200

4. **JOINS SEGUROS**:
   - ✅ BUENOS: ON tabla1.llave = tabla2.llave, ON tabla1.id_producto = tabla2.id_producto
   - ❌ NUNCA: JOIN tabla ON tabla.campo IS NOT NULL (crea producto cartesiano)
   - Usar LEFT JOIN en lugar de JOIN cuando sea posible para evitar pérdida de datos

5. **AGRUPACIONES EFICIENTES**:
   - Usar HAVING para filtrar grupos: HAVING COUNT(*) >= 0, HAVING SUM(campo) >= 0
   - Agrupar por campos con cardinalidad apropiada
   - Evitar GROUP BY con campos de muy alta cardinalidad sin filtros

6. **CTEs (Common Table Expressions) OPTIMIZADOS**:
   - Cada CTE debe tener sus propios filtros temporales y de calidad
   - Evitar CTEs que devuelvan millones de registros
   - Usar filtros específicos en cada CTE antes de hacer JOINs

7. **CAMPOS CALCULADOS EFICIENTES**:
   - Para divisiones: NULLIF(denominador, 0) siempre
   - Para ROUND: ROUND(CAST(campo AS NUMERIC), 2) 
   - Para agregaciones: usar HAVING para filtrar resultados nulos

🔴 **REGLAS ESPECIALES PARA DATOS INCONSISTENTES**:

1. **MANEJO DE CATEGORÍAS INCONSISTENTES**:
   - Usar múltiples variaciones: categoria ILIKE '%farm%' OR categoria ILIKE '%medic%' OR categoria ILIKE '%salud%'
   - Incluir subcategoria en búsquedas: categoria ILIKE '%texto%' OR subcategoria ILIKE '%texto%'

2. **MANEJO DE MARCAS Y PRODUCTOS**:
   - Buscar en descripción también: marca ILIKE '%nike%' OR descripcion_producto ILIKE '%nike%'
   - Usar patrones amplios: ILIKE '%tex%' para encontrar variaciones

3. **VERIFICACIÓN DE EXISTENCIA PREVIA**:
   - Incluir COUNT DISTINCT para verificar variedad de datos
   - Mostrar ejemplos de valores únicos en campos de texto

IMPORTANTES REGLAS PARA FECHAS (CRÍTICO):
1. Las columnas 'fecha' son TEXT, NO DATE
2. Para extraer partes de fecha, SIEMPRE usar CAST:
   - Para año: DATE_PART('year', CAST(fecha AS DATE)) AS año (NO usar alias 'fecha')
   - Para mes: DATE_PART('month', CAST(fecha AS DATE)) AS mes  
   - Para día: DATE_PART('day', CAST(fecha AS DATE)) AS dia
3. Para filtros por fecha: WHERE CAST(fecha AS DATE) >= '2024-01-01'
4. Para rangos de fechas: WHERE CAST(fecha AS DATE) BETWEEN '2024-01-01' AND '2024-12-31'
5. Para fechas relativas: WHERE CAST(fecha AS DATE) >= CURRENT_DATE - INTERVAL '12 months'

EJEMPLOS DE CONSULTAS QUE GARANTIZAN DATOS:

✅ **CONSULTA QUE SIEMPRE TRAE DATOS** (patrón a seguir):
"
SELECT 
    DATE_PART('year', CAST(fecha AS DATE)) AS año,
    DATE_PART('month', CAST(fecha AS DATE)) AS mes,
    id_producto,
    descripcion_producto,
    categoria,
    ROUND(AVG(CAST(precio_venta AS NUMERIC)), 2) AS precio_promedio,
    SUM(CAST(unidades_vendidas AS NUMERIC)) AS total_unidades,
    COUNT(*) AS registros
FROM mis_datos 
WHERE CAST(fecha AS DATE) >= CURRENT_DATE - INTERVAL '12 months'
  AND categoria ILIKE '%comestibles%' -- Búsqueda flexible
  AND precio_venta >= 0              -- Incluye ceros
  AND unidades_vendidas >= 0         -- Incluye ceros
  AND precio_venta IS NOT NULL
  AND id_producto IS NOT NULL
GROUP BY año, mes, id_producto, descripcion_producto, categoria
HAVING COUNT(*) > 0                  -- Muy flexible
ORDER BY año DESC, mes DESC, total_unidades DESC
LIMIT 2000;
"

❌ **CONSULTA RESTRICTIVA** (evitar este patrón):
"
SELECT canal, COUNT(*)
FROM datos_mercado
WHERE categoria = 'Electrónicos'   -- Muy específico
  AND precio_sin_descuento > 0     -- Excluye ceros
  AND CAST(fecha AS DATE) >= CURRENT_DATE - INTERVAL '1 month'  -- Muy corto
GROUP BY canal
HAVING COUNT(*) > 50;              -- Muy restrictivo
"

🔴 **ESTRATEGIA ANTI-CONSULTAS VACÍAS**:

1. **COMENZAR SIMPLE Y EXPANDIR**:
   - Primer intento: Solo filtros esenciales (fecha, IS NOT NULL)
   - Si hay datos: Agregar filtros específicos
   - Si no hay datos: Expandir criterios

2. **INCLUIR CAMPOS DE VERIFICACIÓN**:
   - Siempre incluir COUNT(*) AS total_registros
   - Incluir COUNT(DISTINCT campo) para variedad
   - Mostrar MIN/MAX de campos numéricos

3. **FALLBACK AUTOMÁTICO**:
   - Si búsqueda específica falla, usar búsqueda general
   - Expandir período de tiempo automáticamente
   - Relajar filtros de calidad si es necesario

REGLAS PARA ALIASES DESCRIPTIVOS:
- DATE_PART('year', CAST(fecha AS DATE)) AS año (NO AS fecha)
- DATE_PART('month', CAST(fecha AS DATE)) AS mes (NO AS fecha)
- DATE_PART('quarter', CAST(fecha AS DATE)) AS trimestre
- NUNCA reutilices el nombre 'fecha' como alias cuando extraigas partes de fecha

REGLAS CRÍTICAS DE SINTAXIS:
1. Verifica que todas las estructuras de control (CASE, WITH, subqueries) estén correctamente cerradas.
2. Cada declaración CASE debe tener al menos un WHEN y terminar con END.
3. Asegúrate de que cada paréntesis abierto tenga su paréntesis de cierre correspondiente.
4. Para operaciones aritméticas y funciones como ROUND, usa CAST explícito a NUMERIC: ROUND(CAST(valor AS NUMERIC), 2)
5. Para comparaciones entre columnas de tipos diferentes, usa CAST para asegurar compatibilidad de tipos.
6. Para prevenir división por cero, usa NULLIF: CAST(numerador AS NUMERIC) / NULLIF(CAST(denominador AS NUMERIC), 0)
7. Antes de finalizar, verifica que la consulta esté completa sintácticamente y no termine abruptamente.

IMPORTANTES REGLAS GENERALES:
1. La consulta resultante debe ser una sola consulta SQL válida para PostgreSQL, trata de que conteste la consulta original del usuario, trata de que sea lo más simple posible.
2. No incluyas comentarios con # o -- ni explicaciones, solo la consulta SQL pura.
3. SIEMPRE incluir filtros temporales amplios para maximizar datos disponibles.
4. SIEMPRE usar filtros inclusivos (>=) en lugar de exclusivos (>) para campos numéricos.
5. Usar HAVING muy flexible (>= 0) para garantizar inclusión de datos.
6. Usar búsquedas ILIKE '%texto%' para campos de texto.
7. Procura que tu consulta sea lo más adaptada para presentarse como resultado en excel.
8. Siempre sé explícito en el tipo de datos en todas las operaciones.
9. Si consideras que la consulta es abstracta sin algo que mostrar en gráfica o excel como "Dame el diccionario de datos", entonces regresa un SELECT mostrando las columnas de las tablas.
10. **PRIORIZA SIEMPRE QUE LA CONSULTA TRAIGA DATOS, aunque sea menos específica**.

⚠️ FORMATO DE RESPUESTA:
- Devuelve ÚNICAMENTE el código SQL puro
- NO incluyas comentarios, títulos o explicaciones
- NO uses formato markdown
- El SQL debe empezar directamente con SELECT, WITH, etc.
- Termina con punto y coma
- DEBE incluir filtros temporales amplios y filtros inclusivos para garantizar datos
`; }
